import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import FullScreenLoading from "./components/common/FullScreenLoading";
import useAuth from "./auth/useAuth";
const Login = lazy(() => import("./pages/auth/Login"));
const Home = lazy(() => import("./pages/common/Home"));
const Landing = lazy(() => import("./pages/common/Landing"));
import ProtectedRoute from "./routes/ProtectedRoute";
const NotFound = lazy(() => import("./pages/common/NotFound"));
import Navbar from "./components/layout/Navbar";
import { ToastContainer } from "react-toastify";
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmail"));
const ChangeEmail = lazy(() => import("./pages/auth/ChangeEmail"));
const ChangePassword = lazy(() => import("./pages/auth/ChangePassword"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
import AdminRoute from "./routes/AdminRoute";
const Unauthorized = lazy(() => import("./pages/auth/Unauthorized"));
const UsersList = lazy(() => import("./pages/auth/UsersList"));
const CodeUpdate = lazy(() => import("./pages/auth/PinChange"));
import GuestRoute from "./routes/GuestRoute";
const Products = lazy(() => import("./pages/operations/ProductsPage"));
const StockPage = lazy(() => import("./pages/operations/StockPage"));
const SalesPage = lazy(() => import("./pages/operations/SalesPage"));
const CategoriesPage = lazy(() => import("./pages/operations/Categories"));
const CollectionsPage = lazy(() => import("./pages/operations/Collections"));
const ModelsPage = lazy(() => import("./pages/operations/Models"));
const BrandsPage = lazy(() => import("./pages/operations/Brands"));
const CustomersPage = lazy(() => import("./pages/operations/CustomersPage"));
const SuppliersPage = lazy(() => import("./pages/operations/SuppliersPage"));
const TransactionsPage = lazy(
  () => import("./pages/operations/TransactionsPage"),
);
const PurchaseOrderPage = lazy(
  () => import("./pages/operations/PurchaseOrderPage"),
);
const PaymentVoucherPage = lazy(
  () => import("./pages/operations/PaymentVoucherPage"),
);
const ReceiptVoucherPage = lazy(
  () => import("./pages/operations/ReceiptVoucherPage"),
);
const AccountingPeriodPage = lazy(
  () => import("./pages/operations/AccountingPeriodPage"),
);
const InvoicePage = lazy(() => import("./pages/operations/InvoicePage"));
const PromotionsPage = lazy(() => import("./pages/operations/PromotionsPage"));
const ItemsPage = lazy(() => import("./pages/operations/Items"));
import InventoryValue from "./pages/InventoryValue";
import DailyBranchSales from "./pages/DailyBranchSales";

function RootRoute() {
  const { data, isLoading } = useAuth();

  if (isLoading) {
    return <FullScreenLoading />;
  }

  if (data?.user) {
    return <Navigate to="/home" replace />;
  }

  return <Landing />;
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastContainer />
      <Navbar />
      <Suspense fallback={<FullScreenLoading />}>
        <Routes>
          <Route path="/" element={<RootRoute />} />
          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/login"
            element={
              <GuestRoute>
                <Login />
              </GuestRoute>
            }
          />
          <Route path="/auth/verify-email/:token" element={<VerifyEmail />} />
          <Route
            path="/email-change"
            element={
              <ProtectedRoute>
                <ChangeEmail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/password-change"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path="/code-change"
            element={
              <ProtectedRoute>
                <CodeUpdate />
              </ProtectedRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route
            path="auth/reset-password/:uidb64/:token"
            element={<ResetPassword />}
          />
          <Route
            path="/sales"
            element={
              <ProtectedRoute>
                <SalesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/items-crud"
            element={
              <ProtectedRoute>
                <ItemsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories-crud"
            element={
              <ProtectedRoute>
                <CategoriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/collections-crud"
            element={
              <ProtectedRoute>
                <CollectionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/models-crud"
            element={
              <ProtectedRoute>
                <ModelsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/brands-crud"
            element={
              <ProtectedRoute>
                <BrandsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/transactions"
            element={
              <ProtectedRoute>
                <TransactionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/stock-movement"
            element={
              <ProtectedRoute>
                <StockPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <AdminRoute>
                <Products />
              </AdminRoute>
            }
          />
          <Route
            path="/customers"
            element={
              <ProtectedRoute>
                <CustomersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/suppliers"
            element={
              <ProtectedRoute>
                <SuppliersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-orders"
            element={
              <AdminRoute>
                <PurchaseOrderPage />
              </AdminRoute>
            }
          />
          <Route
            path="/invoices"
            element={
              <AdminRoute>
                <InvoicePage />
              </AdminRoute>
            }
          />
          <Route
            path="/pay-voucher"
            element={
              <AdminRoute>
                <PaymentVoucherPage />
              </AdminRoute>
            }
          />
          <Route
            path="/receipt-voucher"
            element={
              <AdminRoute>
                <ReceiptVoucherPage />
              </AdminRoute>
            }
          />
          <Route
            path="/accounting-period"
            element={
              <AdminRoute>
                <AccountingPeriodPage />
              </AdminRoute>
            }
          />
          <Route
            path="/promotions"
            element={
              <AdminRoute>
                <PromotionsPage />
              </AdminRoute>
            }
          />
          <Route
            path="/users"
            element={
              <AdminRoute>
                <UsersList />
              </AdminRoute>
            }
          />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/inventory-value" element={<InventoryValue />} />
          <Route path="/daily-sales" element={<DailyBranchSales />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
